import { produce } from 'immer';
import invariant from 'invariant';
import nullthrows from 'nullthrows';
import { match } from 'ts-pattern';
import {
  getAdditionalCosts,
  getBaseCountersAbility,
  getDraftCardAbility,
  getEachTurnCardAbility,
  getEndOfGameCardAbility,
  getGainOtherCardAbility,
  getGainSelfCardAbility,
} from './CardAbilities';
import {
  assertCardAffinity,
  assertCardType,
  CardAffinity,
  CardType,
} from './CardTypes';
import { ConflictRoundInfo, EventTypes, GameEvent } from './GameEvents';
import { GameModuleState } from './GameModules';
import { GameOptions } from './GameOptions';
import { Phase, PhaseOrder } from './Phases';
import {
  Bid,
  CardWithID,
  ResolutionSelection,
  TributeCardWithID,
} from './Rules';
import { getTributeLogic } from './TributeLogic';
import { applyCounterDelta, CounterDelta, makeCounterDelta } from './Utility';

export type CardDef = {
  module: string;
  defID: string;
  name: string;
  affinity: CardAffinity;
  type: CardType;
  age: number;
  cost: number;
  favor: number;

  imageName?: string;

  baseEffect?: string;
  turnEffect?: string;
  gainSelfEffect?: string;
  gainOtherEffect?: string;
  draftOtherEffect?: string;
  endOfGameEffect?: string;
  additionalCosts?: string;
};
export type TributeDef = {
  module: string;
  defID: string;
  name: string;
  subtitle: string;
  tokens: number;
  // additional_output
  value?: [number, number, number];

  function: string;
  params: string;

  imageName?: string;
};
export type ModuleDef = {
  key: string;
  name: string;
  description: string;
  state: GameModuleState;
  replaceCore: boolean;
  cardDefs: CardDef[];
  tributeCardDefs: TributeDef[];

  cardsToRemove: string[];
  tributeCardsToRemove: string[];

  gameOptionsToApply: Partial<GameOptions>;
};

export type GameStaticState = {
  id: string;
  version: number;
  type: GameType;
  playerInfos: GamePlayerInfo[];

  options: GameOptions;

  cardDefinitionsByID: { [key: string]: CardDef };
  tributeCardDefinitionsByID: { [key: string]: TributeDef };

  // technically computable from options
  boardSize: number;
  tributeRowSize: number;
  turnsPerAge: number;
};
export type GameType = 'casual' | 'tutorial'; // | 'ranked';

export type GamePlayerInfo = {
  userID: string;
  botType: string | null;
};
// export type CardDefWithIndex = {
//   index: number;
// } & CardDef;
// export type TributeDefWithIndex = {
//   index: number;
// } & TributeDef;

export type GameSecretState = {
  bidsByPlayerID: { [key: string]: Bid };
  resolvesByPlayerID: { [key: string]: ResolutionSelection };

  shuffledDeckIDs: string[];
  shuffledTributeDeckIDs: string[];

  nextCardID: number;

  rngState: readonly number[];
  tutorialState?: TutorialState;
};
export type TutorialState = {
  nextCardIndex: number;
  nextTributeIndex: number;
};

export type GameServerState = {
  gameStartTimestamp: number;
  gameUpdateTimestamp: number;
  gameEndTimestamp: number | null;
  ratingDeltas: GameRatingDelta[] | null;

  readyByUserID: { [key: string]: boolean };

  totalThinkingMillisByPlayerID: { [key: string]: number };
  phaseThinkingMillisByPlayerID: { [key: string]: number };
  phaseStartTimestamp: number;

  events: GameEvent[];
};
export type GameRatingDelta = {
  userID: string;
  score: number;
  originalRating: number;
  newRating: number;
};

export type GameComputedState = {
  age: number;
  turn: number;
  phase: Phase;
  winningPlayerIDs: string[] | null;

  cardsByID: { [key: string]: CardWithID };
  tributeCardsByID: { [key: string]: TributeCardWithID };

  playerStates: GamePlayerComputedState[];
  planningStateByTributeID: { [tributeID: string]: any };

  deck: CardWithID[];
  discard: CardWithID[];
  tributeDeck: TributeCardWithID[];
  tributeDiscard: TributeCardWithID[];

  table: CardWithID[];
  basicPile: CardWithID[];
  tributeRow: TributeCardWithID[];

  // duplicated from static state
  options: GameOptions;
  cardDefinitionsByID: { [key: string]: CardDef };
  tributeCardDefinitionsByID: { [key: string]: TributeDef };

  // duplicated from server state
  events: GameEvent[];
};
export type GamePlayerComputedState = {
  userID: string;

  cards: CardWithID[];
  counters: CounterDelta;

  bid: Bid | null;
  selectedCard: CardWithID | null;
};

export type GameDefinitions = {
  cardDefs: CardDef[];
  tributeCardDefs: TributeDef[];
  modules: ModuleDef[];
};

export function validateCard(card: CardDef): void {
  if (typeof card.defID !== 'string') {
    throw new Error('Invalid card defID');
  }
  assertCardAffinity(card.affinity);
  assertCardType(card.type);
  if (card.age < 0 || card.age > 3) {
    throw new Error(`Invalid card age: ${card.age}`);
  }

  getDraftCardAbility(card);
  getBaseCountersAbility(card);
  getEachTurnCardAbility(card);
  getGainSelfCardAbility(card);
  getDraftCardAbility(card);
  getGainOtherCardAbility(card);
  getAdditionalCosts(card);

  const eog = getEndOfGameCardAbility(card);
  if (eog && !eog.outputs.every((output) => !!output.multiplier?.resource)) {
    throw new Error('Missing prophecy multiplier');
  }
}

export function validateTribute(tribute: TributeDef): void {
  if (typeof tribute.module !== 'string') {
    throw new Error('Invalid tribute module');
  }
  if (typeof tribute.defID !== 'string') {
    throw new Error('Invalid tribute defID');
  }
  if (typeof tribute.name !== 'string') {
    throw new Error('Invalid tribute name');
  }
  if (
    typeof tribute.tokens !== 'number' ||
    tribute.tokens < 0 ||
    tribute.tokens > 3
  ) {
    throw new Error(`Invalid tribute tokens: ${tribute.tokens}`);
  }
  if (typeof tribute.function !== 'string') {
    throw new Error('Invalid tribute function');
  }
  if (typeof tribute.params !== 'string') {
    throw new Error('Invalid tribute params');
  }
  // Value
  // Image

  if (!getTributeLogic(tribute)) {
    throw new Error('Invalid tribute logic');
  }
}
export function mergeModuleCards(
  cardDefinitions: CardDef[],
  tributeCardDefinitions: TributeDef[],
  modules: ModuleDef[],
): {
  cardDefinitions: CardDef[];
  tributeCardDefinitions: TributeDef[];
} {
  let cardDefByID = new Map<string, CardDef>();
  let tributeDefByID = new Map<string, TributeDef>();

  const excludeCore = modules.some((module) => module.replaceCore);

  if (!excludeCore) {
    cardDefinitions.forEach((card_def) => {
      cardDefByID.set(card_def.defID, card_def);
    });
    tributeCardDefinitions.forEach((card_def) => {
      tributeDefByID.set(card_def.defID, card_def);
    });
  }

  modules.forEach((module) => {
    module.cardsToRemove.forEach((card_defID) => {
      cardDefByID.delete(card_defID);
    });
    module.tributeCardsToRemove.forEach((tribute_defID) => {
      tributeDefByID.delete(tribute_defID);
    });

    module.cardDefs.forEach((card_def) => {
      cardDefByID.set(card_def.defID, card_def);
    });
    module.tributeCardDefs.forEach((tribute_def) => {
      tributeDefByID.set(tribute_def.defID, tribute_def);
    });
  });

  return {
    cardDefinitions: Array.from(cardDefByID.values()),
    tributeCardDefinitions: Array.from(tributeDefByID.values()),
  };
}

export function applyGameEvents(
  computedState: GameComputedState,
  events: GameEvent[],
): GameComputedState {
  return produce(computedState, (draftState) => {
    applyGameEventsMutable(draftState, events);
  });
}
export function applyGameEventsMutable(
  mutableState: GameComputedState,
  events: GameEvent[],
): void {
  for (const event of events) {
    mutableState.events.push(event);

    match(event)
      .with({ type: EventTypes.SPAWN_CARDS }, ({ payload }) => {
        const { cardIDDefIDPairs } = payload;
        cardIDDefIDPairs.forEach(([cardID, defID]) => {
          invariant(
            !(cardID in mutableState.cardsByID),
            `Card ID ${cardID} already exists`,
          );
          mutableState.cardsByID[cardID] = {
            ...mutableState.cardDefinitionsByID[defID],
            id: cardID,
          };
        });
      })
      .with({ type: EventTypes.SPAWN_TRIBUTES }, ({ payload }) => {
        const { cardIDDefIDPairs } = payload;
        cardIDDefIDPairs.forEach(([cardID, defID]) => {
          invariant(
            !(cardID in mutableState.tributeCardsByID),
            `Tribute card ID ${cardID} already exists`,
          );
          mutableState.tributeCardsByID[cardID] = {
            ...mutableState.tributeCardDefinitionsByID[defID],
            id: cardID,
          };
        });
      })
      .with({ type: EventTypes.SETUP_DECK }, ({ payload }) => {
        const { cardIDs } = payload;
        mutableState.deck = cardIDs.map((cardID) => {
          return nullthrows(mutableState.cardsByID[cardID]);
        });
        mutableState.discard = [];
      })
      .with({ type: EventTypes.SETUP_TRIBUTE_DECK }, ({ payload }) => {
        const { cardIDs } = payload;
        mutableState.tributeDeck = cardIDs.map((cardID) => {
          return nullthrows(mutableState.tributeCardsByID[cardID]);
        });
        mutableState.tributeDiscard = [];
      })
      .with({ type: EventTypes.SETUP_BASIC_PILE }, ({ payload }) => {
        const { cardIDs } = payload;
        mutableState.basicPile = cardIDs.map((cardID) => {
          return nullthrows(mutableState.cardsByID[cardID]);
        });
      })
      .with({ type: EventTypes.GAIN_LEADER }, ({ payload }) => {
        // deprecated: kept for migrations (v1 game data replay)
        const { userID, cardID, counters } = payload;
        const playerState = getPlayerState(mutableState, userID);

        playerState.cards.push(mutableState.cardsByID[cardID]);
        playerState.counters = applyCounterDelta(
          playerState.counters,
          counters,
        );
      })
      .with({ type: EventTypes.ASSIGN_LEADER }, ({ payload }) => {
        // Similar to PURCHASE_CARD but for leaders (no cost, no table removal)
        const { userID, cardID } = payload;
        const playerState = getPlayerState(mutableState, userID);
        playerState.cards.push(mutableState.cardsByID[cardID]);
      })
      .with({ type: EventTypes.SET_UP_AGE }, ({ payload }) => {
        const { age } = payload;
        mutableState.age = age;
        mutableState.turn = 0;
        mutableState.phase = Phase.SETUP;
      })
      .with({ type: EventTypes.SET_UP_TURN }, ({ payload }) => {
        const { turn } = payload;
        mutableState.turn = turn;
        mutableState.phase = PhaseOrder[0];

        mutableState.playerStates.forEach((playerState) => {
          playerState.bid = null;
          playerState.selectedCard = null;
        });

        mutableState.planningStateByTributeID = {};
      })
      .with({ type: EventTypes.SET_UP_PHASE }, ({ payload }) => {
        const { phase } = payload;
        mutableState.phase = phase;
      })
      .with({ type: EventTypes.TRIBUTES_RESHUFFLED }, () => {
        mutableState.tributeDeck = [
          ...mutableState.tributeDeck,
          ...mutableState.tributeDiscard,
        ];
        mutableState.tributeDiscard = [];
      })
      .with({ type: EventTypes.DEAL_CARD }, ({ payload }) => {
        const { cardID } = payload;
        const card = nullthrows(mutableState.cardsByID[cardID]);
        const deckIndex = mutableState.deck.findIndex((c) => c.id === cardID);
        invariant(deckIndex !== -1, 'card not found in deck');

        mutableState.table.unshift(card);
        mutableState.deck.splice(deckIndex, 1);
      })
      .with({ type: EventTypes.DEAL_TRIBUTE }, ({ payload }) => {
        const { cardID } = payload;
        const tribute = nullthrows(mutableState.tributeCardsByID[cardID]);
        const tributeDeckIndex = mutableState.tributeDeck.findIndex(
          (c) => c.id === cardID,
        );
        invariant(tributeDeckIndex !== -1, 'tribute not found in deck');

        mutableState.tributeRow.unshift(tribute);
        mutableState.tributeDeck.splice(tributeDeckIndex, 1);
      })
      .with({ type: EventTypes.END_OF_PHASE }, ({ payload }) => {
        // noop
      })
      .with({ type: EventTypes.PRODUCTION }, ({ payload }) => {
        const { userID, counters } = payload;
        const playerState = getPlayerState(mutableState, userID);
        playerState.counters = applyCounterDelta(
          playerState.counters,
          counters,
        );
      })
      .with({ type: EventTypes.BID }, ({ payload }) => {
        const { userID, bid, spentMilitary } = payload;
        const playerState = getPlayerState(mutableState, userID);

        playerState.bid = bid;
        playerState.counters.military -= spentMilitary;
      })
      .with({ type: EventTypes.CONFLICT_RESULTS }, ({ payload }) => {
        const { cardID, winningPlayerID: winningUserID, rounds } = payload;
        // In NoDicePlus mode, ties result in no winner (winningUserID is null)
        if (winningUserID !== null) {
          const playerState = getPlayerState(mutableState, winningUserID);
          playerState.selectedCard = mutableState.cardsByID[cardID];
        }
      })
      .with({ type: EventTypes.PURCHASE_CARD }, ({ payload }) => {
        const { userID, cardID, counters } = payload;
        const playerState = getPlayerState(mutableState, userID);

        playerState.counters = applyCounterDelta(
          playerState.counters,
          counters,
        );
        playerState.cards.push(mutableState.cardsByID[cardID]);

        const tableIndex = mutableState.table.findIndex(
          (card) => card.id === cardID,
        );
        if (tableIndex !== -1) {
          mutableState.table.splice(tableIndex, 1);
        }
      })
      .with({ type: EventTypes.GAIN_CARD }, ({ payload }) => {
        const { userID, cardID, counters, cardCounters, triggeredCards } =
          payload;
        const playerState = getPlayerState(mutableState, userID);

        playerState.counters = applyCounterDelta(
          playerState.counters,
          counters,
        );
      })
      .with({ type: EventTypes.DISCARD_CARD }, ({ payload }) => {
        const { cardID } = payload;
        const card = nullthrows(mutableState.cardsByID[cardID]);
        mutableState.discard.push(card);
        const tableIndex = mutableState.table.findIndex((c) => c.id === cardID);
        invariant(tableIndex !== -1, 'card not found in table');
        mutableState.table.splice(tableIndex, 1);
      })
      .with({ type: EventTypes.DISCARD_TRIBUTE }, ({ payload }) => {
        const { cardID } = payload;
        const card = nullthrows(mutableState.tributeCardsByID[cardID]);
        mutableState.tributeDiscard.push(card);
        const tributeRowIndex = mutableState.tributeRow.findIndex(
          (c) => c.id === cardID,
        );
        invariant(tributeRowIndex !== -1, 'card not found in tribute row');
        mutableState.tributeRow.splice(tributeRowIndex, 1);
      })
      .with({ type: EventTypes.TRIBUTE_PLANNING_DATA }, ({ payload }) => {
        const { planningStateByTributeID } = payload;
        mutableState.planningStateByTributeID = planningStateByTributeID;
      })
      .with({ type: EventTypes.GAIN_TRIBUTES }, ({ payload }) => {
        const { userID, counters } = payload;
        const playerState = getPlayerState(mutableState, userID);

        playerState.counters = applyCounterDelta(
          playerState.counters,
          counters,
        );
      })
      .with({ type: EventTypes.END_OF_AGE }, () => {
        mutableState.discard.push(...mutableState.table);
        mutableState.table = [];
        mutableState.discard.push(...mutableState.deck);
        mutableState.deck = [];

        mutableState.tributeDiscard.push(...mutableState.tributeRow);
        mutableState.tributeRow = [];

        if (mutableState.options.wipeTributeRow) {
          mutableState.tributeDiscard.push(...mutableState.tributeDeck);
          mutableState.tributeDeck = [];
        }
      })
      .with({ type: EventTypes.END_OF_GAME }, ({ payload }) => {
        const { winningUserIDs } = payload;
        mutableState.winningPlayerIDs = winningUserIDs;
      })
      .with({ type: EventTypes.END_GAME_EFFECTS }, ({ payload }) => {
        const { userID, counters } = payload;
        const playerState = getPlayerState(mutableState, userID);

        playerState.counters = applyCounterDelta(
          playerState.counters,
          counters,
        );
      })
      .with({ type: EventTypes.DEBUG_SET_PLAYER_COUNTERS }, ({ payload }) => {
        const { userID, counters } = payload;
        const playerState = getPlayerState(mutableState, userID);
        for (const [key, value] of Object.entries(counters)) {
          playerState.counters[key as keyof CounterDelta] = value;
        }
      })
      .with({ type: EventTypes.DEBUG_ADD_CARD }, ({ payload }) => {
        const { userID, cardID } = payload;
        const playerState = getPlayerState(mutableState, userID);
        playerState.cards.push(mutableState.cardsByID[cardID]);
      })
      .with({ type: EventTypes.REFUND_MILITARY }, ({ payload }) => {
        const { userID, refundedMilitary } = payload;
        const playerState = getPlayerState(mutableState, userID);
        playerState.counters.military += refundedMilitary;
      })
      .with({ type: EventTypes.EARN_TOKEN }, ({ payload }) => {
        const { userID, counters } = payload;
        const playerState = getPlayerState(mutableState, userID);

        playerState.counters = applyCounterDelta(
          playerState.counters,
          counters,
        );
      })
      .exhaustive();
  }
}

export function initialGameSecretState(
  rngState: readonly number[],
): GameSecretState {
  return {
    bidsByPlayerID: {},
    resolvesByPlayerID: {},
    shuffledDeckIDs: [],
    shuffledTributeDeckIDs: [],
    nextCardID: 100,
    rngState,
  };
}
export function initialGameServerState(
  staticState: GameStaticState,
): GameServerState {
  return {
    gameStartTimestamp: Date.now(),
    gameUpdateTimestamp: Date.now(),
    gameEndTimestamp: null,
    ratingDeltas: null,

    events: [],

    readyByUserID: {},
    totalThinkingMillisByPlayerID: Object.fromEntries(
      staticState.playerInfos.map((playerInfo) => [playerInfo.userID, 0]),
    ),
    phaseThinkingMillisByPlayerID: {},

    phaseStartTimestamp: Date.now(),
  };
}
export function initialGamePlayerComputedState(
  playerInfo: GamePlayerInfo,
): GamePlayerComputedState {
  return {
    userID: playerInfo.userID,

    cards: [],
    counters: makeCounterDelta(),

    bid: null,
    selectedCard: null,
  };
}
export function initialGameComputedState(
  staticState: GameStaticState,
): GameComputedState {
  return {
    age: 0,
    turn: 0,
    phase: PhaseOrder[0],
    winningPlayerIDs: null,

    cardsByID: {},
    tributeCardsByID: {},

    playerStates: staticState.playerInfos.map(initialGamePlayerComputedState),

    planningStateByTributeID: {},

    deck: [],
    discard: [],
    tributeDeck: [],
    tributeDiscard: [],

    table: [],
    basicPile: [],
    tributeRow: [],

    options: staticState.options,
    cardDefinitionsByID: staticState.cardDefinitionsByID,
    tributeCardDefinitionsByID: staticState.tributeCardDefinitionsByID,

    events: [],
  };
}

export function getPlayerState(
  computedState: GameComputedState,
  userID: string,
): GamePlayerComputedState {
  return nullthrows(
    computedState.playerStates.find(
      (playerState) => playerState.userID === userID,
    ),
  );
}

export type PlayerWarInfo = {
  userID: string;
  totalBonus: number;
  rolls: number[];
  totalFinal: number;
};

export type ClashResults = {
  cardID: string;
  rounds: ConflictRoundInfo[];
  winningPlayerID: string | null; // null when NoDicePlus mode results in a tie
};
